/**
 * Card Selection Service
 *
 * Handles intelligent card selection for exercise sessions.
 * Supports both random selection and FSRS-based selection.
 */

import { getDueCards, getNewCards } from '../fsrs-scheduler';
import type { VocabItem } from '../../types/exercise';
import { supabase } from '../supabase';

export interface CardSelectionConfig {
  /** Total number of cards to select */
  totalCards: number;

  /** Use FSRS algorithm for card selection (prioritize due cards) */
  useFSRS: boolean;

  /** User ID for fetching user-specific cards */
  userId: string;

  /** Optional: Filter by target language */
  targetLanguageId?: string;

  /** Optional: Filter by categories */
  categoryIds?: string[];
}

/**
 * Select cards for an exercise session
 * Supports both FSRS-based and random selection
 */
export async function selectCardsForExercise(
  config: CardSelectionConfig
): Promise<VocabItem[]> {
  if (config.useFSRS) {
    return selectFSRSCards(config);
  }

  return selectRandomCards(config);
}

/**
 * Select cards using FSRS algorithm
 * Prioritizes due cards, then adds new cards if needed
 */
async function selectFSRSCards(config: CardSelectionConfig): Promise<VocabItem[]> {
  const { totalCards, userId } = config;

  // 1. Get due cards (cards that should be reviewed now)
  const dueCards = await getDueCards(userId, totalCards);

  // Convert to VocabItem format
  let selectedCards = dueCards.map(card => convertToVocabItem(card));

  // Apply filters if specified
  if (config.targetLanguageId) {
    selectedCards = selectedCards.filter(
      card => card.targetLanguage?.id === config.targetLanguageId
    );
  }

  if (config.categoryIds && config.categoryIds.length > 0) {
    selectedCards = selectedCards.filter(
      card => card.category && config.categoryIds!.includes(card.category.id)
    );
  }

  // 2. If we don't have enough due cards, add new cards
  if (selectedCards.length < totalCards) {
    const needed = totalCards - selectedCards.length;
    const newCards = await getNewCards(userId, needed * 2); // Get extra to account for filters

    let newVocabItems = newCards.map(card => convertToVocabItem(card));

    // Apply same filters
    if (config.targetLanguageId) {
      newVocabItems = newVocabItems.filter(
        card => card.targetLanguage?.id === config.targetLanguageId
      );
    }

    if (config.categoryIds && config.categoryIds.length > 0) {
      newVocabItems = newVocabItems.filter(
        card => card.category && config.categoryIds!.includes(card.category.id)
      );
    }

    // Add new cards up to needed amount
    selectedCards = [...selectedCards, ...newVocabItems.slice(0, needed)];
  }

  return selectedCards.slice(0, totalCards);
}

/**
 * Select random cards (original behavior)
 */
async function selectRandomCards(config: CardSelectionConfig): Promise<VocabItem[]> {
  const { totalCards, userId, targetLanguageId, categoryIds } = config;

  // Build query
  let query = supabase
    .from('flashcards')
    .select('*, flashcard_target_languages(*), flashcard_categories(*)')
    .eq('user_id', userId);

  // Apply filters
  if (targetLanguageId) {
    query = query.eq('target_language_id', targetLanguageId);
  }

  if (categoryIds && categoryIds.length > 0) {
    query = query.in('category_id', categoryIds);
  }

  // Fetch cards
  const { data, error } = await query.limit(totalCards * 3); // Get extra for randomization

  if (error) {
    console.error('Error fetching cards:', error);
    return [];
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Convert to VocabItem and shuffle
  const vocabItems = data.map(card => convertToVocabItem(card));
  const shuffled = shuffleArray(vocabItems);

  return shuffled.slice(0, totalCards);
}

/**
 * Convert database flashcard to VocabItem
 */
function convertToVocabItem(card: any): VocabItem {
  return {
    id: card.id,
    primary_text: card.primary_text,
    target_text: card.target_text,
    primary: card.primary_text,
    target: card.target_text,
    category_id: card.category_id,
    target_language_id: card.target_language_id,
    user_id: card.user_id,
    created_at: card.created_at,
    updated_at: card.updated_at,
    transliteration: card.transliteration,
    phonetic: card.phonetic,
    examples: card.examples || [],
    level: card.level,
    targetLanguage: card.flashcard_target_languages ? {
      id: card.flashcard_target_languages.id,
      name: card.flashcard_target_languages.name,
      code: card.flashcard_target_languages.code,
    } : undefined,
    category: card.flashcard_categories ? {
      id: card.flashcard_categories.id,
      name: card.flashcard_categories.name,
    } : undefined,
    // FSRS fields
    due: card.due,
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review,
  } as VocabItem;
}

/**
 * Fisher-Yates shuffle
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Get count of due cards for a user
 * Useful for displaying "X cards due" in UI
 */
export async function getDueCardsCount(
  userId: string,
  targetLanguageId?: string,
  categoryIds?: string[]
): Promise<number> {
  const now = new Date();

  let query = supabase
    .from('flashcards')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .lte('due', now.toISOString());

  if (targetLanguageId) {
    query = query.eq('target_language_id', targetLanguageId);
  }

  if (categoryIds && categoryIds.length > 0) {
    query = query.in('category_id', categoryIds);
  }

  const { count, error } = await query;

  if (error) {
    console.error('Error getting due cards count:', error);
    return 0;
  }

  return count || 0;
}
